import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  FaUniversity,
  FaBitcoin,
  FaMoneyBillWave,
  FaGlobeAmericas
} from "react-icons/fa";

import "./Checkout.css";
import { BACKEND_URL } from "../../../config";

const CURRENCY = "€";

function Payment() {
  const navigate = useNavigate();

  // No payment method is pre-selected — user must actively choose one.
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    setCartItems(cart);
  }, []);

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.qty, 0);
  const shipping = cartItems.length > 0 ? 25 : 0;
  const discount = selectedPayment === "Crypto" ? subtotal * 0.1 : 0;
  const total = subtotal + shipping - discount;

  // ======================================================
  // DELETE STALE PENDING ORDER
  // Runs whenever the user switches to a DIFFERENT payment method
  // than the one their current pending order was created for.
  // This prevents duplicate / ghost orders from piling up in admin
  // (e.g. Remitly order #38 created, user goes back, picks Western
  // Union instead → order #38 must be deleted before #39 is made).
  // ======================================================
  const deletePendingOrderIfExists = async () => {
    const existing = JSON.parse(localStorage.getItem("pendingOrder")) || {};

    if (!existing.orderId) return;

    try {
      await fetch(
        `${BACKEND_URL}/api.php?module=orders&action=delete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ id: existing.orderId })
        }
      );
    } catch (err) {
      // Best-effort cleanup — even if this fails, we don't block
      // the user from continuing with their newly chosen method.
      console.error("Failed to delete stale pending order:", err);
    } finally {
      localStorage.removeItem("pendingOrder");
    }
  };

  // ======================================================
  // GENERIC ORDER CREATION FOR ALL "MANUAL TRANSFER" METHODS
  // (Bank Transfer / Remitly / Western Union)
  // ======================================================
  const createOrderAndContinue = async (paymentMethod, detailsRoute) => {
    try {

      const user = JSON.parse(localStorage.getItem("user"));

      const response = await fetch(
        `${BACKEND_URL}/api.php?module=orders&action=create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            userId: user?.id,
            shipping: JSON.parse(localStorage.getItem("shippingData")),
            medical: JSON.parse(localStorage.getItem("medicalData")),
            paymentMethod,
            cart: cartItems,
            subtotal,
            shippingCharge: shipping,
            discount,
            total,
            status: "payment_pending_confirmation"
          })
        }
      );

      const data = await response.json();

      if (data.success) {
        const reference = `ORD-${String(data.order_id).padStart(6, "0")}`;

        localStorage.setItem(
          "pendingOrder",
          JSON.stringify({
            orderId: data.order_id,
            reference,
            paymentMethod
          })
        );

        navigate(detailsRoute);
      } else {
        alert("Order creation failed");
      }
    } catch (err) {
      alert("Server error");
    }
  };

  // Shared handler for any "manual transfer" method.
  // - If a pending order for the SAME method already exists, just resume it.
  // - If a pending order for a DIFFERENT method exists, delete it first
  //   (so it never lingers as a duplicate in admin), then create a fresh one.
  const handleManualTransfer = async (paymentMethod, detailsRoute) => {

    const existing =
      JSON.parse(localStorage.getItem("pendingOrder")) || {};

    if (
      existing.orderId &&
      existing.paymentMethod === paymentMethod
    ) {
      navigate(detailsRoute);
      return;
    }

    // Switching method (or no pending order yet) — clean up any stale
    // order from a previously selected method before creating a new one.
    if (existing.orderId) {
      await deletePendingOrderIfExists();
    }

    localStorage.setItem("paymentMethod", paymentMethod);

    createOrderAndContinue(paymentMethod, detailsRoute);
  };

  const PAYMENT_OPTIONS = [
    {
      name: "Bank Transfer",
      icon: <FaUniversity />,
      action: () => handleManualTransfer("Bank Transfer", "/checkout/bank-details")
    },
    {
      name: "Remitly",
      icon: <FaMoneyBillWave />,
      action: () => handleManualTransfer("Remitly", "/checkout/remitly-details")
    },
    {
      name: "Western Union",
      icon: <FaGlobeAmericas />,
      action: () => handleManualTransfer("Western Union", "/checkout/western-union-details")
    },
    {
      name: "Crypto",
      icon: <FaBitcoin />,
      text: "Pay by Crypto (10% Discount)",
      action: async () => {
        localStorage.setItem("paymentMethod", "Crypto");
        // If a manual-transfer order was already created, delete it
        // before moving into the crypto flow.
        await deletePendingOrderIfExists();
        navigate("/checkout/crypto-selection");
      }
    }
  ];

  return (
    <>
      <Helmet>
        <title>Payment | Checkout</title>
        <meta name="description" content="Secure payment page" />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://kamagrastar.eu/payment" />
      </Helmet>

      <div className="checkout-page">

        <div className="checkout-steps">
          <div className="step done" onClick={() => navigate("/checkout")}>
            <span>✓</span><p>Shipping</p>
          </div>
          <div className="line active"></div>

          <div className="step done" onClick={() => navigate("/checkout/medical")}>
            <span>✓</span><p>Medical</p>
          </div>
          <div className="line active"></div>

          <div className="step active">
            <span>3</span><p>Payment</p>
          </div>
        </div>

        <div className="checkout-wrapper">

          {/* LEFT */}
          <div className="checkout-left">
            <h2>Payment Method</h2>

            {PAYMENT_OPTIONS.map((item) => (
              <div
                key={item.name}
                className={`payment-box ${selectedPayment === item.name ? "active" : ""}`}
                onClick={() => {
                  setSelectedPayment(item.name);
                  item.action();
                }}
              >
                <div className="payment-head">
                  <div className="payment-left">
                    {item.icon}
                    <span>{item.text || item.name}</span>
                  </div>
                  <div className="circle"></div>
                </div>
              </div>
            ))}

            <p className="payment-note">
              Select a payment method to continue. Your order will be placed
              after you confirm payment on the next page.
            </p>
          </div>

          {/* RIGHT */}
          <div className="checkout-right">

            <div className="summary-card">
              <h3>Order Summary</h3>

              {cartItems.map((item, index) => (
                <div className="summary-item" key={index}>
                  <img src={item.image} alt={item.productName} loading="lazy" />
                  <div className="summary-info">
                    <h4>{item.productName}</h4>
                    <p>{item.qty} × {item.packSize}</p>
                  </div>
                  <span>{CURRENCY}{(item.price * item.qty).toFixed(2)}</span>
                </div>
              ))}

              <div className="summary-total">
                <div>
                  <span>Subtotal</span>
                  <span>{CURRENCY}{subtotal.toFixed(2)}</span>
                </div>

                <div>
                  <span>Shipping</span>
                  <span>{CURRENCY}{shipping.toFixed(2)}</span>
                </div>

                {discount > 0 && (
                  <div>
                    <span>Crypto Discount</span>
                    <span>-{CURRENCY}{discount.toFixed(2)}</span>
                  </div>
                )}

                <div className="grand-total">
                  <span>Total</span>
                  <span>{CURRENCY}{total.toFixed(2)}</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

export default Payment;
